import { LinkSlashIcon } from "@heroicons/react/24/outline";
import React from "react";

import { Button, Dialog, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

interface ConnectEHRRequiredDialogProps {
  open: boolean;
  // Primary intent — the user wants to connect their EHR now. Opens the EHR
  // login flow inline; they return to the import step once connected.
  onConnectEHR: () => void;
  // Defer — the user closes the dialog and stays on the import step (no auto
  // progress); they can connect later from here or skip the step.
  onDismiss: () => void;
}

// Dependency nudge shown when an EHR-compatible BCBA who skipped the
// Connect-EHR step tries to import clients: importing requires an active EHR
// connection. This is NOT an error — the user did nothing wrong by skipping, so
// it uses warning (amber) styling, not error red. There is intentionally no X
// and no backdrop dismiss: the user picks a path via the two buttons (connect
// now, or close and stay on the import step). (PROA-1081 onboarding.)
export const ConnectEHRRequiredDialog: React.FC<ConnectEHRRequiredDialogProps> = ({
  open,
  onConnectEHR,
  onDismiss,
}) => (
  <Dialog
    open={open}
    setOpen={() => {}}
    showCloseButton={false}
    // No title — pull the body up over the Dialog's (now empty) header row so the
    // space above the icon matches the 24px padding at the bottom of the dialog.
    title={undefined}
    size="sm"
    primaryButton={undefined}
    secondaryButton={undefined}
    dialogClassName="sm:max-w-[380px]"
    bodyClassName="-mt-4 pb-6"
  >
    <div className="flex flex-col">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
        <LinkSlashIcon className="h-5 w-5 text-amber-700" />
      </div>
      <Headline size="small" className="mt-4">
        You&apos;ll need to connect your EHR first.
      </Headline>
      <Paragraph size="small" className="mt-2 text-text-secondary">
        Importing clients requires an active EHR connection. Connect yours now to continue with importing your client
        list.
      </Paragraph>

      <hr className="my-5 border-border-subtle" />

      <div className="flex flex-col gap-2">
        <Button
          text="Go connect EHR"
          variant="primary"
          buttonStyle="filled"
          onClick={onConnectEHR}
          className="w-full justify-center"
        />
        <Button
          text="I'll do this later"
          variant="secondary"
          buttonStyle="border"
          onClick={onDismiss}
          className="w-full justify-center"
        />
      </div>
    </div>
  </Dialog>
);
